import { useMemo, useState } from 'react';
import { useFormikContext } from 'formik';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import {
  DividerWithText,
  InputText,
  InputTextArea,
} from '@farmshare/ui-components';
import { useRecoilValue } from 'recoil';
import { userState } from 'state';

import {
  type Cutsheet,
  EnumCutsheetAnimalSpecies,
  EnumCutsheetInspectionLevels,
  ProcessorCapability,
  ProcessorSettings,
} from 'lib/graphql';

import {
  CutsheetWizardFormValues,
  WizardContext,
  WizardPrimalCut,
} from '../types';
import { useBuildCutsData } from '../services/use-build-cuts-data';
import { CutsheetWizardDialog } from '../cutsheet-wizard-dialog';
import { CutCard } from '../components/cut-card';
import { SpecificationOptionCard } from '../components/specification-option-card';
import { TrimOptionCard } from '../components/trim-option-card';
import { ExtraOptionCard } from '../components/extra-option-card';
import { ReviewContent } from '../components/review-content';
import { useWizardConfig } from '../context';
import { useCutGroups } from '../hooks/use-cut-groups';
import { useSpecificationGroups } from '../hooks/use-specification-groups';
import { useExtrasOptions } from '../hooks/use-extras-options';
import { useTrimOptions } from '../hooks/use-trim-options';
import { HeadsSelection } from '../components/heads-selection';
import type { CutsheetRequest } from 'pages/scheduling/_views/edit-cutsheets';
import { get } from 'lodash';
import { useUserState } from 'providers/user-state-provider';

const HiddenNote: React.FC<{ name: string; label: string; title: string }> = ({
  name,
  label,
  title,
}) => {
  const [showTextArea, setShowTextArea] = useState(false);
  const { values } = useFormikContext<CutsheetWizardFormValues>();

  const value = useMemo(() => get(values, name), [name, values]);

  // If the text area is not shown and there's no value, show the "Add Note" button
  if (!showTextArea && !value) {
    return (
      <Button
        variant="text"
        size="small"
        onClick={() => setShowTextArea(true)}
        sx={{ mt: 3, textAlign: 'center' }}
        fullWidth
      >
        Add a Custom Note
      </Button>
    );
  }

  return (
    <Box sx={{ mt: 3 }}>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mb: 1, fontSize: '0.875rem' }}
      >
        {title}
      </Typography>
      <InputTextArea
        label={label}
        nameOverride={name}
        rows={3}
        showLabel={false}
      />
    </Box>
  );
};

// Helper component for specification content
function SpecificationContent({
  options,
  selectedValue,
  onChangeSpec,
  measurementUnit = undefined,
  specKey,
  imageUrl,
  onSelectionChange = undefined,
}: {
  options: Array<{ value: string; label: string; description: string }>;
  selectedValue: string;
  onChangeSpec: (value: string) => void;
  measurementUnit?: string;
  specKey: string;
  imageUrl?: string;
  onSelectionChange?: (specKey?: string, specValue?: string) => void;
}) {
  return (
    <>
      {options.map((option) => (
        <SpecificationOptionCard
          key={option.value}
          option={option}
          checked={selectedValue === option.value}
          onChange={(e) => {
            if (e.target.checked) {
              onChangeSpec(option.value);
              if (onSelectionChange) {
                onSelectionChange(specKey, option.value);
              }
            }
          }}
          measurementUnit={measurementUnit}
          name={specKey}
          imageUrl={imageUrl}
        />
      ))}
    </>
  );
}

export function WizardStepsBuilder({
  open,
  onClose,
  isPreviewMode = false,
  renderInline = false,
  onCutsheetCreated = undefined,
  onSubmitWithCutsheet = undefined,
  userCutsheetRequests = undefined,
  onUseAdvanced = undefined,
  cutsheetType = undefined,
  userId = undefined,
  buyerId = undefined,
  producerId = undefined,
  templateCutsheet = undefined,
}: {
  open: boolean;
  onClose: () => void;
  isPreviewMode?: boolean;
  renderInline?: boolean;
  onCutsheetCreated?: () => void;
  onSubmitWithCutsheet?: (cutsheet: Cutsheet, selectedHeads: string[]) => void;
  userCutsheetRequests?: CutsheetRequest[];
  onUseAdvanced?: () => void;
  cutsheetType?: 'vendor' | 'producer' | 'buyer' | 'user';
  userId?: string;
  buyerId?: string;
  producerId?: string;
  templateCutsheet?: Cutsheet;
}): JSX.Element {
  const { values, setFieldValue, resetForm } =
    useFormikContext<CutsheetWizardFormValues>();
  const { animalSpecies, capabilities, inspectionLevel, settings } =
    useWizardConfig();
  const user = useRecoilValue(userState);

  // Calculate unique buyers from cutsheet requests to determine if buyer option should be shown
  const uniqueBuyers = useMemo(() => {
    if (!userCutsheetRequests) return [];
    const buyerSet = new Set<string>();
    userCutsheetRequests.forEach((request) => {
      request.cutsheets.forEach((cutsheet) => {
        if (cutsheet?.contactUser?._id) {
          buyerSet.add(cutsheet.contactUser._id as unknown as string);
        }
      });
    });
    return Array.from(buyerSet);
  }, [userCutsheetRequests]);

  const singleBuyerId = uniqueBuyers.length === 1 ? uniqueBuyers[0] : null;

  /**
   * Build cuts data with blocker relationships.
   *
   * This hook fetches primal cuts, organizes them by primal name, and enriches each cut
   * with blocker information from the PrimalCutBlocker collection. Blockers define which
   * cuts are mutually exclusive (e.g., T-Bone blocks NY Strip + Filet Mignon because they
   * come from the same section of the animal).
   *
   * Note: settings is a single ProcessorSettings object. The hook uses it to query
   * the vendor's blocker relationships via settings.vendor._id.
   */
  const { cutsByPrimalName: cutGroups } = useBuildCutsData(
    animalSpecies as unknown as EnumCutsheetAnimalSpecies,
    (capabilities ?? []) as ProcessorCapability[],
    inspectionLevel as unknown as EnumCutsheetInspectionLevels,
    settings as ProcessorSettings,
  );

  const selectedIds = useMemo(
    () => (values.cuts ?? []).map((c) => c?._id).filter(Boolean),
    [values.cuts],
  );

  // Use cut groups hook for blocker logic
  const { isMutuallyExclusiveGroup, isDisabled, getBlockedByText } =
    useCutGroups(selectedIds, cutGroups);

  const onToggleCheckbox =
    (cut: WizardPrimalCut) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const { checked } = e.target;
      const next = checked
        ? [...(values.cuts ?? []), cut]
        : (values.cuts ?? []).filter((c) => c?._id !== cut._id);
      return setFieldValue('cuts', next, true);
    };

  const onToggleRadio =
    (group: (typeof cutGroups)[number], cut: WizardPrimalCut) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { checked } = e.target;
      const groupIds = new Set(group.cuts.map((c) => c._id));
      const filtered = (values.cuts ?? []).filter((c) => !groupIds.has(c?._id));

      // If unchecked (clicking already selected radio), just remove from group
      if (!checked) {
        return setFieldValue('cuts', filtered, true);
      }

      // If checked, add to selection
      return setFieldValue('cuts', [...filtered, cut], true);
    };

  // Derive specification options from capabilities for this animal/inspection level
  const activeCapability = useMemo(
    () =>
      (capabilities ?? []).find(
        (cap) =>
          cap?.animalSpecies === animalSpecies &&
          cap?.inspectionLevel === inspectionLevel,
      ),
    [capabilities, animalSpecies, inspectionLevel],
  );

  // Use specification groups hook
  const { specGroups, onChangeSpec } = useSpecificationGroups(
    activeCapability,
    values,
    setFieldValue,
  );

  const cutsSteps = cutGroups.map((group) => {
    const groupInputType = isMutuallyExclusiveGroup(group)
      ? 'radio'
      : 'checkbox';

    // Get primal description directly from the cut's primal object
    // All cuts in a group share the same primal, so we can use the first cut's primal
    const groupDescription = group?.description; // Removing description access to fix type error

    // Auto-advance disabled to allow users to add notes
    const shouldAutoAdvance = false;

    return {
      id: group.primalId,
      title: group.primalName,
      subHead: groupDescription,
      imageUrl: group.imageUrl ?? undefined,
      instructions: '',
      autoAdvanceOnSelection: shouldAutoAdvance,
      content: (
        <>
          {group.cuts.map((cut) => (
            <CutCard
              key={cut._id}
              cut={cut}
              inputType={groupInputType}
              checked={selectedIds.includes(cut._id)}
              disabled={isDisabled(cut)}
              blockedByText={getBlockedByText(cut)}
              onChange={(e) => {
                if (groupInputType === 'radio') {
                  onToggleRadio(group, cut)(e);
                } else {
                  onToggleCheckbox(cut)(e);
                }
              }}
            />
          ))}
          <HiddenNote
            name={`notes.${group.primalId}`}
            label="Cut Notes"
            title={`Add any notes for ${group.primalName}.`}
          />
        </>
      ),
    };
  });

  // Add a Specifications step after cut groups if data is present
  const specSteps = useMemo(
    () => {
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.log(
          '[Debug] Available spec groups:',
          specGroups.map((sg) => ({
            key: sg.key,
            title: sg.title,
            isRequired: sg.isRequired,
          })),
        );
      }

      return specGroups.length
        ? specGroups.map((sg) => ({
            id: `spec:${sg.key}`,
            title: sg.title,
            subHead: sg.description,
            imageUrl: sg.imageUrl ?? undefined,
            instructions: '',
            // Auto-advance disabled to allow users to add notes
            autoAdvanceOnSelection: false,
            // Add validation for required specifications
            canGoNext: (context: WizardContext) => {
              if (!sg.isRequired) return true;
              // Get fresh values ONLY from the context parameter, not from closure
              const selectedValue = (
                context.formValues as {
                  selectedSpecifications?: Record<string, unknown>;
                }
              )?.selectedSpecifications?.[sg.key];

              if (process.env.NODE_ENV === 'development') {
                // eslint-disable-next-line no-console
                console.log('[Debug] Spec validation:', {
                  specKey: sg.key,
                  isRequired: sg.isRequired,
                  selectedValue,
                  allSpecs: (context.formValues as any)?.selectedSpecifications,
                  allSpecsKeys: Object.keys(
                    (context.formValues as any)?.selectedSpecifications || {},
                  ),
                  contextFormValues: context.formValues,
                });
              }

              return (
                selectedValue !== undefined &&
                selectedValue !== null &&
                selectedValue !== ''
              );
            },
            content: (context: {
              onSelectionChange?: (
                specKey?: string,
                specValue?: string,
              ) => void;
            }) => (
              <>
                <SpecificationContent
                  options={sg.options}
                  selectedValue={String(
                    values.selectedSpecifications?.[sg.key] || '',
                  )}
                  onChangeSpec={(value) => onChangeSpec(sg.key)(value)}
                  measurementUnit={sg.measurementUnit}
                  specKey={sg.key}
                  imageUrl={sg.imageUrl}
                  onSelectionChange={context.onSelectionChange}
                />
                <HiddenNote
                  name={`notes.${sg.key}`}
                  label="Specification Notes"
                  title={`Add any notes for ${sg.title}.`}
                />
              </>
            ),
          }))
        : [];
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [specGroups, values.selectedSpecifications],
  );

  // Use extras options hook
  const { extrasOptions, onToggleExtra, onQuantityChange } = useExtrasOptions(
    activeCapability,
    values,
    setFieldValue,
  );

  const { isProcessor } = useUserState();

  const extrasStep = useMemo(
    () =>
      extrasOptions.length
        ? [
            {
              id: 'extras',
              title: 'Extras',
              subHead: activeCapability?.extrasDescription || '',
              imageUrl: activeCapability?.extrasImageUrl ?? undefined,
              instructions: '',
              content: (
                <>
                  {extrasOptions.map((option) => {
                    const selectedExtra = values.extras?.find(
                      (e) => e.name === option.label,
                    );
                    return (
                      <ExtraOptionCard
                        key={option.value}
                        option={option}
                        checked={!!selectedExtra?.isActive}
                        quantity={selectedExtra?.quantity}
                        onChange={(e) =>
                          onToggleExtra(option.value, e.target.checked)
                        }
                        onQuantityChange={onQuantityChange}
                      />
                    );
                  })}
                  <HiddenNote
                    name="notes.extras"
                    label="Extra Notes"
                    title="Add any notes about your extra selections."
                  />
                </>
              ),
            },
          ]
        : [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [extrasOptions, values.extras, onToggleExtra, onQuantityChange],
  );

  // Use trim options hook
  const {
    trimOptions,
    onToggleTrim,
    isTrimSelected,
    getTrimSelection,
    onTrimRankUp,
    onTrimRankDown,
    onTrimQuantityChange,
    trimValidation,
  } = useTrimOptions(activeCapability, values, setFieldValue);

  const trimStep = useMemo(
    () =>
      trimOptions.length
        ? [
            {
              id: 'trim',
              title: 'Trim',
              subHead: activeCapability?.trimDescription || '',
              imageUrl: activeCapability?.trimImageUrl ?? undefined,
              instructions: '',
              content: (
                <>
                  {Array.isArray(values.trim) && values.trim.length >= 2 && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      <strong>Why prioritize?</strong> Priority determines which
                      trim options are fulfilled first if your animal
                      doesn&apos;t yield enough trim for all selections.
                    </Alert>
                  )}
                  {trimOptions.map((option) => {
                    const selection = getTrimSelection(option.value);
                    const totalSelected = Array.isArray(values.trim)
                      ? values.trim.length
                      : 0;

                    return (
                      <TrimOptionCard
                        key={option.value}
                        option={option}
                        checked={isTrimSelected(option.value)}
                        onChange={(e) =>
                          onToggleTrim(option.value, e.target.checked, option)
                        }
                        selectionMode={
                          values.trimSelection || 'allowSelectionByWeight'
                        }
                        rank={selection?.rank}
                        quantity={selection?.quantity}
                        totalSelected={totalSelected}
                        onRankUp={() => onTrimRankUp(option.value)}
                        onRankDown={() => onTrimRankDown(option.value)}
                        onQuantityChange={(quantity) =>
                          onTrimQuantityChange(option.value, quantity)
                        }
                      />
                    );
                  })}
                  {values.trimSelection === 'allowSelectionByPercentage' &&
                    values.trim &&
                    values.trim.length > 0 && (
                      <Box
                        sx={{
                          mt: 2,
                          p: 2,
                          bgcolor: trimValidation.isValid
                            ? 'success.main'
                            : 'error.main',
                          borderRadius: 1,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            color: 'white',
                            fontWeight: 600,
                          }}
                        >
                          Total: {trimValidation.total.toFixed(1)}%
                          {!trimValidation.isValid &&
                            ` - ${trimValidation.message}`}
                        </Typography>
                      </Box>
                    )}
                  <HiddenNote
                    name="notes.trim"
                    label="Trim Notes"
                    title="Add any notes about your trim selections."
                  />
                </>
              ),
              canGoNext: async () => trimValidation.isValid,
            },
          ]
        : [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      trimOptions,
      values.trim,
      values.trimSelection,
      trimValidation.isValid,
      trimValidation.total,
      trimValidation.message,
    ],
  );

  // Build review step config; content is a component that uses navigation context at render time
  const reviewStep = useMemo(
    () => [
      {
        id: 'review',
        title: 'Review Your Selections',
        subHead:
          'Please review your selections below before submitting your cutsheet.',
        instructions: '',
        content: (
          <ReviewContent
            cutGroups={cutGroups}
            selectedIds={selectedIds}
            specGroups={specGroups}
            trimOptions={trimOptions}
            extrasOptions={extrasOptions}
            values={values}
            isPreviewMode={isPreviewMode}
          />
        ),
        onNext: isPreviewMode
          ? async () => {
              // In preview mode, reset the form when advancing from the review step
              resetForm();
            }
          : undefined,
      },
    ],
    [
      cutGroups,
      selectedIds,
      specGroups,
      trimOptions,
      extrasOptions,
      values,
      isPreviewMode,
      resetForm,
    ],
  );

  // Determine if "replace original" option should be shown
  const shouldShowReplaceOriginal = useMemo(() => {
    if (!templateCutsheet) return false;

    // Always show for processors
    if (isProcessor) return true;

    // For non-processors, only show if the cutsheet's user matches current user
    if (templateCutsheet.user && user?._id) {
      return String(templateCutsheet.user) === String(user._id);
    }

    return false;
  }, [templateCutsheet, isProcessor, user]);

  // Naming step - where user enters cutsheet name and selects cutsheet type (for processors)
  const namingStep = useMemo(
    () => [
      {
        id: 'naming',
        title: 'Name Your Cutsheet',
        subHead:
          'Give your cutsheet a memorable name and select which animal heads to apply it to.',
        instructions: '',
        content: (
          <Box>
            <TextField
              fullWidth
              label="Cutsheet Name"
              placeholder="e.g., My Standard Beef Cutsheet"
              value={values.name || ''}
              onChange={(e) => setFieldValue('name', e.target.value, true)}
              variant="outlined"
              required
              sx={{ mb: 3 }}
            />

            {isProcessor && (
              <InputText
                label="Price Per Pound (Optional)"
                placeholder="e.g., 5.50"
                type="number"
                nameOverride="pricePerPound"
              />
            )}

            {shouldShowReplaceOriginal && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={values.replaceOriginal ?? false}
                    onChange={(e) =>
                      setFieldValue('replaceOriginal', e.target.checked, true)
                    }
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1">
                      Replace Original Cutsheet
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Update the original cutsheet instead of creating a new one
                    </Typography>
                  </Box>
                }
                sx={{ mb: 2, mt: 2, display: 'flex', alignItems: 'flex-start' }}
              />
            )}

            {!cutsheetType && (
              <FormControl component="fieldset" fullWidth sx={{ mb: 2 }}>
                <FormLabel component="legend" sx={{ mb: 1 }}>
                  Cutsheet Type
                </FormLabel>

                <RadioGroup
                  value={values.cutsheetType || ''}
                  sx={{ gap: 1 }}
                  onChange={(e) =>
                    setFieldValue('cutsheetType', e.target.value, true)
                  }
                >
                  {isProcessor && (
                    <>
                      <FormControlLabel
                        value="vendor"
                        control={<Radio />}
                        label={
                          <Box>
                            <Typography variant="body1">
                              House Cutsheet
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Save as a house cutsheet available to all
                              customers
                            </Typography>
                          </Box>
                        }
                      />
                      {producerId && (
                        <FormControlLabel
                          value="producer"
                          control={<Radio />}
                          label={
                            <Box>
                              <Typography variant="body1">
                                Producer Cutsheet
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Save against the producer for future use for
                                them and their customers
                              </Typography>
                            </Box>
                          }
                        />
                      )}
                    </>
                  )}
                  {buyerId && (
                    <FormControlLabel
                      value="buyer"
                      control={<Radio />}
                      label={
                        <Box>
                          <Typography variant="body1">
                            Buyer Cutsheet
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Save against the buyer for their future use
                          </Typography>
                        </Box>
                      }
                    />
                  )}
                  {!isProcessor && (
                    <FormControlLabel
                      value="user"
                      control={<Radio />}
                      label={
                        <Box>
                          <Typography variant="body1">My Cutsheets</Typography>
                          <Typography variant="body2" color="text.secondary">
                            Save to your personal custom cutsheets
                          </Typography>
                        </Box>
                      }
                    />
                  )}
                </RadioGroup>
              </FormControl>
            )}
          </Box>
        ),
        canGoNext: async () => {
          // Validate that name is not empty
          if (!values.name || values.name.trim().length === 0) {
            return false;
          }
          // Validate cutsheet type is selected (either from prop or form)
          if (!cutsheetType && !values.cutsheetType) {
            return false;
          }
          // If heads are available, at least one should be selected
          if (userCutsheetRequests && userCutsheetRequests.length > 0) {
            return values.selectedHeads && values.selectedHeads.length > 0;
          }
          return true;
        },
      },
    ],
    [
      values.name,
      values.cutsheetType,
      values.selectedHeads,
      values.replaceOriginal,
      setFieldValue,
      user,
      singleBuyerId,
      userCutsheetRequests,
      cutsheetType,
      shouldShowReplaceOriginal,
      isProcessor,
    ],
  );

  const stepsOverride = useMemo(
    () => [
      ...specSteps,
      ...cutsSteps,
      ...extrasStep,
      ...trimStep,
      ...reviewStep,
      ...namingStep,
    ],
    [specSteps, cutsSteps, extrasStep, trimStep, reviewStep, namingStep],
  );

  return (
    <CutsheetWizardDialog
      open={open}
      onClose={onClose}
      settings={settings}
      capabilities={capabilities}
      animalSpecies={animalSpecies}
      inspectionLevel={inspectionLevel}
      stepsOverride={stepsOverride}
      renderInline={renderInline}
      isPreviewMode={isPreviewMode}
      onCutsheetCreated={onCutsheetCreated}
      onSubmitWithCutsheet={onSubmitWithCutsheet}
      userCutsheetRequests={userCutsheetRequests}
      onUseAdvanced={onUseAdvanced}
      cutsheetType={cutsheetType}
      userId={userId}
      buyerId={buyerId}
      producerId={producerId}
      templateCutsheet={templateCutsheet}
    />
  );
}
